import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Calendar, Users, Eye, Clock, RefreshCw, TrendingUp } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { AdminPanel } from '@/components/AdminPanel';

interface AnalyticsData {
  total_visits: number;
  unique_visitors: number;
  returning_visitors: number;
  identified_visitors: number;
  avg_session_duration: number;
  conversion_rate: number;
  top_pages: Array<{ page: string; visits: number }>;
  top_referrers: Array<{ referrer: string; visits: number }>;
  device_breakdown: Record<string, number>;
  browser_breakdown: Record<string, number>;
  country_breakdown: Record<string, number>;
  conversion_breakdown: Array<{ type: string; count: number }>;
}

interface VisitorRecord {
  id: string;
  session_id: string;
  visitor_fingerprint: string;
  page_url: string;
  page_title: string;
  referrer: string;
  device_type: string;
  browser: string;
  os: string;
  country: string;
  city: string;
  lead_email?: string;
  lead_name?: string;
  lead_phone?: string;
  lead_company?: string;
  conversion_type?: string;
  created_at: string;
}

const Analytics = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [visitors, setVisitors] = useState<VisitorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Fetch analytics summary
      const { data: analyticsData, error: analyticsError } = await supabase
        .rpc('get_visitor_analytics', {
          start_date: startDate.toISOString(),
          end_date: new Date().toISOString(),
        });

      if (analyticsError) throw analyticsError;
      if (analyticsData && analyticsData.length > 0) {
        setAnalytics(analyticsData[0] as AnalyticsData);
      }

      // Fetch recent visitors
      const { data: visitorsData, error: visitorsError } = await supabase
        .from('visitor_tracking')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(100);

      if (visitorsError) throw visitorsError;
      setVisitors(visitorsData || []);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching analytics:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background/95">
      <Navigation />
      <AdminPanel />
      
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">
                Visitor Analytics Dashboard
              </h1>
              <p className="text-muted-foreground">
                Track and analyze every visitor to your site
              </p>
            </div>
            <Button onClick={fetchAnalytics} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Date Range Selector */}
          <div className="flex gap-2">
            <Button
              variant={dateRange === '7d' ? 'default' : 'outline'}
              onClick={() => setDateRange('7d')}
            >
              Last 7 Days
            </Button>
            <Button
              variant={dateRange === '30d' ? 'default' : 'outline'}
              onClick={() => setDateRange('30d')}
            >
              Last 30 Days
            </Button>
            <Button
              variant={dateRange === '90d' ? 'default' : 'outline'}
              onClick={() => setDateRange('90d')}
            >
              Last 90 Days
            </Button>
          </div>

          {/* Stats Overview */}
          {analytics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Visits</CardTitle>
                  <Eye className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.total_visits}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Unique Visitors</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.unique_visitors}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Identified Leads</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.identified_visitors}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analytics.conversion_rate}% conversion
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Returning Visitors</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.returning_visitors}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg. Session</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatDuration(Number(analytics.avg_session_duration) || 0)}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Detailed Analytics Tabs */}
          <Tabs defaultValue="leads" className="space-y-4">
            <TabsList>
              <TabsTrigger value="leads">Identified Leads</TabsTrigger>
              <TabsTrigger value="pages">Top Pages</TabsTrigger>
              <TabsTrigger value="referrers">Referrers</TabsTrigger>
              <TabsTrigger value="devices">Devices</TabsTrigger>
              <TabsTrigger value="browsers">Browsers</TabsTrigger>
              <TabsTrigger value="visitors">All Visitors</TabsTrigger>
            </TabsList>

            <TabsContent value="leads" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Identified Leads for Remarketing</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Phone</th>
                          <th className="text-left p-2">Company</th>
                          <th className="text-left p-2">Conversion Type</th>
                          <th className="text-left p-2">First Visit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitors
                          .filter(v => v.lead_email)
                          .reduce((acc, visitor) => {
                            // Group by email to show unique leads
                            const existing = acc.find(v => v.lead_email === visitor.lead_email);
                            if (!existing) {
                              acc.push(visitor);
                            }
                            return acc;
                          }, [] as VisitorRecord[])
                          .map((visitor) => (
                            <tr key={visitor.lead_email} className="border-b hover:bg-muted/50">
                              <td className="p-2 font-medium">{visitor.lead_name || 'N/A'}</td>
                              <td className="p-2">{visitor.lead_email}</td>
                              <td className="p-2">{visitor.lead_phone || 'N/A'}</td>
                              <td className="p-2">{visitor.lead_company || 'N/A'}</td>
                              <td className="p-2">
                                <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                                  {visitor.conversion_type}
                                </span>
                              </td>
                              <td className="p-2 text-xs text-muted-foreground">
                                {new Date(visitor.created_at).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    {visitors.filter(v => v.lead_email).length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No identified leads yet. Visitors will appear here when they submit a form.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {analytics?.conversion_breakdown && analytics.conversion_breakdown.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Conversion Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analytics.conversion_breakdown.map((conv, index) => (
                        <div key={index} className="flex justify-between items-center p-2 border-b">
                          <span className="text-sm capitalize">{conv.type.replace('_', ' ')}</span>
                          <span className="text-sm font-bold">{conv.count} conversions</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="pages" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Top Pages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics?.top_pages?.map((page, index) => (
                      <div key={index} className="flex justify-between items-center p-2 border-b">
                        <span className="text-sm truncate flex-1">{page.page}</span>
                        <span className="text-sm font-bold">{page.visits} visits</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="referrers" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Top Referrers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics?.top_referrers?.map((ref, index) => (
                      <div key={index} className="flex justify-between items-center p-2 border-b">
                        <span className="text-sm truncate flex-1">{ref.referrer}</span>
                        <span className="text-sm font-bold">{ref.visits} visits</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="devices" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Device Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics?.device_breakdown && Object.entries(analytics.device_breakdown).map(([device, count]) => (
                      <div key={device} className="flex justify-between items-center p-2 border-b">
                        <span className="text-sm capitalize">{device}</span>
                        <span className="text-sm font-bold">{count} visits</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="browsers" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Browser Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analytics?.browser_breakdown && Object.entries(analytics.browser_breakdown).map(([browser, count]) => (
                      <div key={browser} className="flex justify-between items-center p-2 border-b">
                        <span className="text-sm">{browser}</span>
                        <span className="text-sm font-bold">{count} visits</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="visitors" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Visitors (Last 100)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Time</th>
                          <th className="text-left p-2">Page</th>
                          <th className="text-left p-2">Lead Info</th>
                          <th className="text-left p-2">Device</th>
                          <th className="text-left p-2">Browser</th>
                          <th className="text-left p-2">Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitors.map((visitor) => (
                          <tr key={visitor.id} className={`border-b ${visitor.lead_email ? 'bg-primary/5' : ''}`}>
                            <td className="p-2">
                              {new Date(visitor.created_at).toLocaleString()}
                            </td>
                            <td className="p-2 truncate max-w-xs">{visitor.page_title || visitor.page_url}</td>
                            <td className="p-2">
                              {visitor.lead_email ? (
                                <div className="text-xs">
                                  <div className="font-bold text-primary">{visitor.lead_name || visitor.lead_email}</div>
                                  <div className="text-muted-foreground">{visitor.lead_email}</div>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">Anonymous</span>
                              )}
                            </td>
                            <td className="p-2 capitalize">{visitor.device_type}</td>
                            <td className="p-2">{visitor.browser}</td>
                            <td className="p-2">{visitor.city || visitor.country || 'Unknown'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Analytics;
